// @flow strict

import type {ConditionNode as ConditionNodeType} from 'src/types/chatbot';
import type {NodeEditorProps} from './node-editor-v2.jsx';

import type {EditableRuleset} from 'src/types/ruleset';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import invariant from 'invariant';
import lodashKeyBy from 'lodash/keyBy';

import {useChatbotStateContext} from '../chatbot-state-context';

import {keyBy} from 'src/utils/map';
import classify from 'src/utils/classify';

// $FlowFixMe[nonstrict-import]
import {
  createConditionNode,
  createEditableGraph,
  getNumericOperatorOptions,
  getStringOperatorOptions,
  getOperatorOptions,
  dateResponseTypes,
  numericResponseTypes,
  getBooleanOptions,
  findOption,
  updateExpression,
  createExpression,
  validateRuleset,
  initializeEditableRuleset,
  getHasAncestorJobMatchQuestionNode,
} from '../utils';

// $FlowFixMe[nonstrict-import]
import {validateNode} from '../node-editor/validation';

import {UnstyledButton} from 'src/components/lib/new-button';

import {Button} from 'src/designSystem2021Components/buttons.jsx';

// $FlowFixMe[nonstrict-import]
import VariablePickerSelect from 'src/components/lib/variable-picker/select-2.jsx';
//$FlowFixMe[untyped-import]
import {TokenWithChip} from 'src/components/lib/markdown-editor/dynamic-text.jsx';
// $FlowFixMe[nonstrict-import]
import {NewVariableDropdownRow} from 'src/components/conversation-builder/lib/variable-picker/variable-picker.jsx';

// $FlowFixMe[nonstrict-import]
import {Input} from 'src/designSystem2021Components/input.jsx';
import {Select} from 'src/designSystem2021Components/select.jsx';

import {
  // $FlowFixMe[nonstrict-import]
  useChatbotVariables,
  // $FlowFixMe[nonstrict-import]
  useEditorState,
  // $FlowFixMe[nonstrict-import]
  writebackDataTypeToResponseType,
} from '../node-editor/reducer';

import CloseIcon from 'src/images/icons/cancel-icon.svg';

import css from './node-editor-v2.css';
import borderlessClassNames from 'src/components/lib/token-list-input/borderless.css';


export default function ConditionNode({
  graph,
  onCancel,
  readOnly,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps): React.Node {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const {state, dispatch} = useChatbotStateContext();
  const numericOperatorOptions = getNumericOperatorOptions(t);
  const stringOperatorOptions = getStringOperatorOptions(t);
  const operatorOptions = getOperatorOptions(t);

  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const defaultNode =
    state.editingNode !== null && editingNodeData?.type === 'condition'
      ? editingNodeData
      : createConditionNode();

  const [editorState, editorDispatch] = useEditorState<ConditionNodeType>({
    defaultNode,
  });

  const {node} = editorState;

  const setNode = (payload: {...ConditionNodeType}) => {
    editorDispatch({type: 'update', payload});
  };

  const entityType = graph.anchor_entity_type;
  const {url_params_map: urlParamsMap} = graph.sourcing_config || {};

  const [ruleset, setRuleset] = React.useState<EditableRuleset>(() => {
    const {condition_ruleset} = node;
    const {expressions} = condition_ruleset;
    return expressions.length > 0
      ? initializeEditableRuleset(condition_ruleset)
      : {...condition_ruleset, expressions: [createExpression()]};
  });

  const editableGraph = createEditableGraph(graph);

  const hasAncestorJobMatchQuestionNode = getHasAncestorJobMatchQuestionNode(
    editableGraph,
    defaultNode,
    state.insertingNode?.edgeId,
  );

  const dynamicLabels = useChatbotVariables(
    entityType,
    {
      showDiscoverAttributes: hasAncestorJobMatchQuestionNode,
      showExternalVariables: Boolean(graph.flow_config.api_bot_enabled),
    },
    urlParamsMap,
  );
  const dynamicLabelMap = React.useMemo(() => keyBy(dynamicLabels, 'value'));

  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  // $FlowFixMe[class-object-subtyping]
  const validation = validateRuleset(ruleset, dynamicLabelMap);
  const nodeValidation = validateNode(node, editableGraph);

  const {expressions} = ruleset;

  const updateRuleset = (expressionId, updates) => {
    setRuleset(updateExpression(ruleset, expressionId, updates));
  };

  const nodeOptions = React.useMemo(
    () =>
      [...graph.nodes.values()]
        .filter((node) => node.type === 'question')
        .map((node) => ({
          label: node.name,
          value: node.id,
        })),
    [graph.nodes],
  );

  const handleSaveNode = () => {
    if (validation || nodeValidation) {
      setHasSubmitted(true);
    } else {
      const payload = {...node, condition_ruleset: ruleset};
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            //$FlowFixMe[incompatible-call] if the ruleset passes validation, it's castable.
            payload,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload,
          });
    }
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  const allOperatorOptions = [
    ...operatorOptions,
    ...stringOperatorOptions,
    ...numericOperatorOptions,
  ];
  const allOperatorOptionsByValue = lodashKeyBy(allOperatorOptions, 'value');

  const expressionStatements = expressions.map((expression) => {
    const valueLabel = expression.dynamic_field
      ? `<${expression.dynamic_field}>`
      : findOption(nodeOptions, expression.node_id_answer)?.label ?? '';
    const operatorString =
      expression.operator &&
      allOperatorOptionsByValue[expression.operator].label;
    const conditionValues = Array.isArray(expression.condition_value)
      ? expression.condition_value.length
        ? expression.condition_value.join(' or ')
        : t('empty', 'empty')
      : expression.condition_value;

    return (
      <span key={expression.id}>
        <strong>{valueLabel}</strong>{' '}
        {expression.is_not ? t('is_not', 'is not') : t('is', 'is')}{' '}
        {operatorString} <strong>{conditionValues} </strong>
      </span>
    );
  });

  const conditionalStatement = (
    <p>
      {`${t('Conditional_statement', 'Conditional statement')}:`}{' '}
      {expressionStatements.map((statement, index) => (
        <>
          {index !== 0 && <span>{` ${t('AND', 'AND')} `}</span>}
          {statement}
        </>
      ))}
    </p>
  );
  return (
    <div className={css.questionNodeContainer}>
      <div className={css.section}>
        <Input
          label={t('Node_Title', 'Node Title')}
          value={node.name}
          onChange={(event) =>
            setNode({...node, name: event.currentTarget.value})
          }
        />
        {hasSubmitted && nodeValidation?.name && (
          <div className={css.errorText}>
            {nodeValidation.name
              ? nodeValidation.name.errors?.includes('EMPTY')
                ? `${t(
                    'This_node_must_have_a_name',
                    'This node must have a name',
                  )}.`
                : `${t(
                    'This_name_is_already_taken',
                    'This name is already taken',
                  )}.`
              : `${t(
                  'Node_names_must_be_unique_40_characters_maximum',
                  'Node names must be unique. 40 characters maximum',
                )}.`}
          </div>
        )}
      </div>
      <div className={css.section}>
        <div className={css.ruleset}>
          {expressions.map((expression, index) => {
            const condition_value =
              expression.condition_value instanceof Array
                ? expression.condition_value
                : [expression.condition_value].filter(Boolean);

            let dataType = null;
            if (expression.node_id_answer) {
              const node = editableGraph.nodes.get(expression.node_id_answer);
              invariant(
                node?.type === 'question',
                `${t(
                  'Node_in_an_expression_must_be_a_question',
                  'Node in an expression must be a `question`',
                )}.`,
              );
              dataType = node.response_type;
            } else {
              const dynamicLabel = dynamicLabelMap.get(
                expression.dynamic_field,
              );
              const atsDataType = dynamicLabel?.type;
              dataType =
                atsDataType && atsDataType in writebackDataTypeToResponseType
                  ? writebackDataTypeToResponseType[atsDataType]
                  : null;
            }

            if (!dataType) {
              dataType = 'string';
            }

            const isDate = dateResponseTypes.has(dataType);
            const isNumeric = numericResponseTypes.has(dataType);
            const operatorOptionsForDataType = isNumeric
              ? numericOperatorOptions
              : dataType === 'string'
              ? stringOperatorOptions
              : operatorOptions;

            const notOptions = getBooleanOptions(expression.operator);

            // for numbers, when we pick gt/lt we can only have one option
            const varPickerOptionsLimited =
              dataType === 'number' &&
              ['greater_than', 'less_than'].includes(expression.operator);

            return (
              <>
                <div className={css.expressionContainer} key={expression.id}>
                  <div className={css.prefix}>
                    {!readOnly && ruleset.expressions.length > 1 ? (
                      <UnstyledButton
                        className={css.remove}
                        onClick={() => {
                          setRuleset({
                            ...ruleset,
                            expressions: ruleset.expressions.filter(
                              ({id}) => id !== expression.id,
                            ),
                          });
                        }}
                      >
                        <CloseIcon />
                      </UnstyledButton>
                    ) : (
                      <div />
                    )}

                    <span className={css.if}>
                      {index === 0 ? t('If', 'If') : t('And', 'And')}
                    </span>
                  </div>
                  <div className={css.expression} key={expression.id}>
                    {'node_id_answer' in expression ? (
                      <Select
                        searchable
                        autofocus={!expression.node_id_answer}
                        placeholder={t('Select_node', 'Select node')}
                        options={nodeOptions}
                        value={findOption(
                          nodeOptions,
                          expression.node_id_answer,
                        )}
                        onChange={(option) => {
                          updateRuleset(expression.id, {
                            node_id_answer: option?.value,
                          });
                        }}
                        disabled={readOnly}
                        resolveKey={(option) => String(option.value)}
                      />
                    ) : 'dynamic_field' in expression ? (
                      <VariablePickerSelect
                        classNames={{outerContainer: css.bumpedSelect}}
                        baseEntityType={entityType}
                        searchable
                        autofocus={!expression.dynamic_field}
                        baseClassNames={borderlessClassNames}
                        placeholder={t('Search_variables', 'Search variables')}
                        limit={1}
                        values={[
                          dynamicLabelMap.get(expression.dynamic_field),
                        ].filter(Boolean)}
                        options={dynamicLabels}
                        onChange={(options) => {
                          updateRuleset(expression.id, {
                            // $FlowFixMe indexing into array
                            dynamic_field: options[0]?.value,
                          });
                        }}
                        disabled={readOnly}
                      />
                    ) : (
                      <Select
                        placeholder={`${t('Node_or_var', 'Node or var')}`}
                        options={[
                          {value: 'node', label: t('Node', 'Node')},
                          {value: 'variable', label: t('Variable', 'Variable')},
                        ]}
                        onChange={(option) => {
                          if (!option) {
                            return;
                          }
                          if (option.value === 'node') {
                            updateRuleset(expression.id, {
                              node_id_answer: null,
                            });
                          } else if (option.value === 'variable') {
                            updateRuleset(expression.id, {
                              dynamic_field: null,
                            });
                          }
                        }}
                        value={''}
                        disabled={readOnly}
                      />
                    )}

                    <Select
                      options={notOptions}
                      value={notOptions.find(
                        ({value}) => expression.is_not === value,
                      )}
                      onChange={(option) => {
                        if (option) {
                          updateRuleset(expression.id, {
                            is_not: option.value,
                          });
                        }
                      }}
                      disabled={readOnly}
                    />

                    <Select
                      options={operatorOptionsForDataType}
                      value={operatorOptionsForDataType.find(
                        ({value}) => value === expression.operator,
                      )}
                      onChange={(option) => {
                        if (option) {
                          updateRuleset(expression.id, {
                            operator: option.value,
                          });
                        }
                      }}
                      disabled={readOnly}
                    />
                    <VariablePickerSelect
                      classNames={{outerContainer: css.bumpedSelect}}
                      inputProps={{
                        type: isDate ? 'date' : isNumeric ? 'number' : 'text',
                      }}
                      baseEntityType={entityType}
                      isExpandable
                      searchable
                      searchOnEmptyString
                      allowArbitraryValues
                      showSugggestionOnPaste
                      allowMultiArbitraryValues
                      // when options are limited to 1 set this to false
                      // so that a phantom input doesn't appear
                      showValuesOutside={!varPickerOptionsLimited}
                      limit={varPickerOptionsLimited ? 1 : undefined}
                      baseClassNames={borderlessClassNames}
                      placeholder={!isDate ? 'Empty' : null}
                      values={condition_value
                        .map((value) => {
                          const variable = value.match(/^<(.*)>$/);
                          if (variable) {
                            return dynamicLabelMap.get(variable[1]);
                          }
                          return {value, arbitrary: true, label: value};
                        })
                        .filter(Boolean)}
                      options={dynamicLabels.filter(
                        (label) =>
                          dataType === 'string' || label.type === dataType,
                      )}
                      onChange={(options) => {
                        const newOptions = options.reduce((acc, option) => {
                          if (option.arbitrary) {
                            acc.push(option.value);
                          } else if (option.multiArbitrary) {
                            const values = option.value
                              .trim()
                              .split(/[\,\n]/)
                              .filter((value) => value !== '');
                            acc.push(...values);
                          } else {
                            acc.push(`<${option.value}>`);
                          }
                          return acc;
                        }, []);

                        updateRuleset(expression.id, {
                          condition_value: [...new Set(newOptions)],
                        });
                      }}
                      components={{
                        Token: TokenWithChip,
                        Suggestion: NewVariableDropdownRow,
                      }}
                      disabled={readOnly}
                    />
                  </div>
                </div>
                {hasSubmitted &&
                  validation?.expressions?.[String(index)]?.errors?.map(
                    (err) => <div className={css.errorText}>{err}</div>,
                  )}
              </>
            );
          })}
        </div>

        {!readOnly && (
          <Button
            size="small"
            type="tertiary"
            className={css.addButton}
            onClick={() => {
              setRuleset({
                ...ruleset,
                expressions: [...expressions, createExpression()],
              });
            }}
          >{`+ ${t('And', 'And')}`}</Button>
        )}
      </div>

      <div className={classify(css.section, css.conditionStatement)}>
        {conditionalStatement}
      </div>

      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={hasSubmitted && (!!validation || !!nodeValidation)}
        >
          {t('Save', 'Save')}
        </Button>
      </div>
    </div>
  );
}
